import {
  EntityLookupSearchCriteria,
  TableFilterLookupObjectBody,
} from '@ui-kit'
import {
  ICopyEligibilityConfigurationPayload,
  IEntityCategoryPayload,
  IFetchEligibilityRulesParams,
  IInsuranceTypesEntityQueryPayload,
  IUpdateRulePayloadType,
} from '../types'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT } from '../utils/environment'
import { parseRuleCompositeKey } from '../utils/functions'
import { ENTITY_NAMES } from '../constants'

export const fetchCriteria = async () => {
  return await axiosInstance.get(
    `${API_END_POINT.CORE}v1/eligibility_configuration/criteria`
  )
}

export const fetchRulesOffCriteria = async (
  params: IFetchEligibilityRulesParams
) => {
  return await axiosInstance.get(
    `${API_END_POINT.CORE}v1/eligibility_configuration/rules?scopeId=${params.id}`
  )
}

export const fetchInsuranceTypeCoverages = async () => {
  return await axiosInstance.get(
    `${API_END_POINT.CORE}v1/eligibility_configuration/plan_type_criteria/coverages`
  )
}

export const fetchInsuranceTypeCriteriaGrouped = async (
  configIdOrRuleKey: string
) => {
  const parsed = parseRuleCompositeKey(configIdOrRuleKey)
  const configId = parsed?.config_id || configIdOrRuleKey
  const query = new URLSearchParams({ configId: String(configId) })

  return await axiosInstance.get(
    `${
      API_END_POINT.CORE
    }v1/eligibility_configuration/plan_type_criteria/grouped?${query.toString()}`
  )
}

export const fetchInsuranceTypesEntity = async (
  payload: IInsuranceTypesEntityQueryPayload
) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${payload.entity_name}`,
    payload
  )
}

export const fetchInsuranceTypes = async (payload: {
  page: number
  size: number
}) => {
  return await fetchInsuranceTypesEntity({
    related_entities: {},
    select: ['name', 'eligibility_configuration_id'],
    entity_name: 'insurance_types',
    page: payload.page,
    size: payload.size,
  })
}

export const markInsuranceTypeAsCoverageType = async (
  insuranceTypeId: string | number
) => {
  return await axiosInstance.patch(
    `${API_END_POINT.CORE}v1/insurance_types/${encodeURIComponent(
      String(insuranceTypeId)
    )}/coverage_type?isCoverageType=true`
  )
}

export const fetchCoverageReasons = async (coverageId: string | number) => {
  return await axiosInstance.get(
    `${
      API_END_POINT.CORE
    }v1/eligibility_configuration/plan_type_criteria/coverage_reasons?coverageId=${encodeURIComponent(
      String(coverageId)
    )}`
  )
}

export const fetchRule = async (rule_name: string) => {
  const parsed = parseRuleCompositeKey(rule_name)
  const basePath = `${
    API_END_POINT.CORE
  }v1/eligibility_configuration/${parsed?.ruleName?.toLowerCase()}`
  const query = new URLSearchParams({
    configId: parsed?.config_id,
  })

  return await axiosInstance.get(`${basePath}?${query.toString()}`)
}

export const updateCorrespondingRules = async (
  payload: IUpdateRulePayloadType
) => {
  return await axiosInstance.put(
    `${API_END_POINT.CORE}v1/eligibility_configuration`,
    payload
  )
}

export const fetchEntityCategory = async (payload: IEntityCategoryPayload) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/category`,
    payload
  )
}

export const fetchResidencyEntity = async (payload: IEntityCategoryPayload) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${payload?.entity_name}`,
    payload
  )
}

export const copyEligibilityConfiguration = async (
  payload: ICopyEligibilityConfigurationPayload
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/eligibility_configuration/copy`,
    payload
  )
}

export const fetchEligibilityConfigurationListing = async (payload: {
  filters: EntityLookupSearchCriteria[]
  related_entities: TableFilterLookupObjectBody
  select: string[]
  entity_name: string
  page: number
  size: number
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${ENTITY_NAMES.PAP_ELIGIBILITY_CONFIGURATIONS}`,
    payload
  )
}

export const checkOrCreateEligibilityScope = async (payload: {
  program_id: string | number
  product_id: string | number
  service_id: string | number
  sub_service_id: string | number
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/eligibility_configuration/scope`,
    payload
  )
}

export const fetchEntityViewData = async (
  entityName: string,
  payload: {
    select: string[]
    page?: number
    size?: number
    filters?: EntityLookupSearchCriteria[]
    related_entities?: TableFilterLookupObjectBody
    layout_id?: number
  }
) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${entityName}`,
    {
      ...payload,
      entity_name: entityName,
    }
  )
}
export const fetchEvaluationOptions = async (type: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER}category-value/type?categoryType=${type}`
  )
}

export const fetchEnrollmentTypesBySubService = async (
  subServiceId: string | number
) => {
  return await axiosInstance.get(
    `${
      API_END_POINT.CORE
    }v1/eligibility_configuration/enrollment_types?subServiceId=${encodeURIComponent(
      String(subServiceId)
    )}`
  )
}
